// src/pages/Bookings.tsx
import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Alert,
  CircularProgress,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper
} from '@mui/material';
import { useAuth } from '../context/AuthContext';
import { API_BASE_URL } from '../utils/api';

interface Booking {
  id: number;
  service: string;
  date: string;
  time: string;
  message: string;
  status: string;
  createdAt: string;
  user: {
    name: string;
  };
  provider: {
    businessName: string;
  };
}

interface ServiceProvider {
  id: number;
  businessName: string;
  services: string[];
  categories: Array<{
    category: {
      id: number;
      name: string;
    };
  }>;
}

const Bookings: React.FC = () => {
  const location = useLocation();
  const { user } = useAuth();
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [providers, setProviders] = useState<ServiceProvider[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [openBookingDialog, setOpenBookingDialog] = useState(false);
  const [bookingData, setBookingData] = useState({
    providerId: '',
    service: '',
    date: '',
    time: '',
    message: ''
  });
  const [message, setMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null);

  // Check if we have a pre-selected provider from category selection
  const preSelectedProviderId = location.state?.providerId;

  const fetchBookings = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/bookings`);
      if (!response.ok) throw new Error('Failed to fetch bookings');
      const data = await response.json();
      setBookings(data);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchProviders = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/providers`);
      if (!response.ok) throw new Error('Failed to fetch providers');
      const data = await response.json();
      setProviders(data);
    } catch (err: any) {
      console.error('Failed to fetch providers:', err);
    }
  };

  useEffect(() => {
    fetchBookings();
    fetchProviders();
    
    // If we have a pre-selected provider, open the booking dialog
    if (preSelectedProviderId) {
      setBookingData(prev => ({ ...prev, providerId: preSelectedProviderId.toString() }));
      setOpenBookingDialog(true);
    }
  }, [preSelectedProviderId]);

  const handleSubmitBooking = async () => {
    try {
      // Combine date and time into a full ISO string
      const dateTime = new Date(`${bookingData.date}T${bookingData.time}`).toISOString();

      const response = await fetch(`${API_BASE_URL}/bookings`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...bookingData,
          providerId: Number(bookingData.providerId), // ensure providerId is a number
          userId: user?.id,
          date: dateTime, // send as ISO string
          status: 'pending'
        }),
      });
      
      if (!response.ok) throw new Error('Failed to submit booking');
      
      setMessage({ type: 'success', text: 'Booking submitted successfully!' });
      setBookingData({ providerId: '', service: '', date: '', time: '', message: '' });
      setOpenBookingDialog(false);
      fetchBookings();
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to submit booking' });
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'accepted': return 'success';
      case 'pending': return 'warning';
      case 'declined': return 'error';
      default: return 'default';
    }
  };

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}><CircularProgress /></Box>;
  if (error) return <Alert severity="error">{error}</Alert>;

  return (
    <Container>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          Bookings
        </Typography>
        <Button 
          variant="contained" 
          onClick={() => setOpenBookingDialog(true)}
        >
          New Booking
        </Button>
      </Box>
      
      {message && (
        <Alert severity={message.type} sx={{ mb: 2 }} onClose={() => setMessage(null)}>
          {message.text}
        </Alert>
      )}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Provider</TableCell>
              <TableCell>Service</TableCell>
              <TableCell>Date</TableCell>
              <TableCell>Time</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Message</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {bookings.map((booking) => (
              <TableRow key={booking.id}>
                <TableCell>{booking.provider.businessName}</TableCell>
                <TableCell>{booking.service}</TableCell>
                <TableCell>{new Date(booking.date).toLocaleDateString()}</TableCell>
                <TableCell>{booking.time}</TableCell>
                <TableCell>
                  <Chip 
                    label={booking.status} 
                    color={getStatusColor(booking.status) as any}
                    size="small"
                  />
                </TableCell>
                <TableCell>{booking.message}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Booking Dialog */}
      <Dialog open={openBookingDialog} onClose={() => setOpenBookingDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>New Booking</DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mb: 2, mt: 1 }}>
            <InputLabel>Select Service Provider</InputLabel>
            <Select
              value={bookingData.providerId}
              label="Select Service Provider"
              onChange={(e) => setBookingData({ ...bookingData, providerId: e.target.value, service: '' })}
            >
              {providers.map((provider) => (
                <MenuItem key={provider.id} value={provider.id}>
                  {provider.businessName}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          
          {bookingData.providerId && (
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Select Service</InputLabel>
              <Select
                value={bookingData.service}
                label="Select Service"
                onChange={(e) => setBookingData({ ...bookingData, service: e.target.value })}
              >
                {providers
                  .find(p => p.id === Number(bookingData.providerId))
                  ?.services.map((service) => (
                    <MenuItem key={service} value={service}>
                      {service}
                    </MenuItem>
                  ))
                }
              </Select>
            </FormControl>
          )}
          
          <TextField
            fullWidth
            type="date"
            label="Date"
            value={bookingData.date}
            onChange={(e) => setBookingData({ ...bookingData, date: e.target.value })}
            InputLabelProps={{ shrink: true }}
            sx={{ mb: 2 }}
            inputProps={{ min: new Date().toISOString().split('T')[0] }}
          />
          <TextField
            fullWidth
            type="time"
            label="Time"
            value={bookingData.time}
            onChange={(e) => setBookingData({ ...bookingData, time: e.target.value })}
            InputLabelProps={{ shrink: true }}
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            label="Message (Optional)"
            multiline
            rows={3}
            value={bookingData.message}
            onChange={(e) => setBookingData({ ...bookingData, message: e.target.value })}
            placeholder="Describe your service needs..."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenBookingDialog(false)}>Cancel</Button>
          <Button 
            onClick={handleSubmitBooking} 
            variant="contained"
            disabled={!bookingData.providerId || !bookingData.service || !bookingData.date || !bookingData.time}
          >
            Book Appointment
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default Bookings;